import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    DnsOutlined,
    SearchRounded,
    SportsEsportsRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import {
    LOBBY_OURO_BORDER,
    LOBBY_OURO_SHADOW,
    LOBBY_OURO_SURFACE,
    LOBBY_OURO_TOP_GLOW,
    MOBILE_NAV_MQ,
} from "./lobbyPageStyles";

const STATUS_ENTER = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.32, ease: "easeOut" },
};

function resolveSignal(value) {
    if (value === null || value === undefined) {
        return { value: 0, color: colors.state.danger.main, isLive: false };
    }

    return {
        value,
        color: value > 0 ? colors.state.success.main : colors.primary.main,
        isLive: value > 0,
    };
}

export default function SystemStatusBar({ platformStats, embedded = false }) {
    const { serversOnline, playersSearching, activeMatches } = platformStats || {};
    const items = [
        {
            label: "Servers",
            icon: DnsOutlined,
            ...resolveSignal(serversOnline),
        },
        {
            label: "Searching",
            icon: SearchRounded,
            ...resolveSignal(playersSearching),
        },
        {
            label: "Active matches",
            mobileLabel: "Matches",
            icon: SportsEsportsRounded,
            ...resolveSignal(activeMatches),
        },
    ];

    return (
        <Box
            component={motion.div}
            initial={STATUS_ENTER.initial}
            animate={STATUS_ENTER.animate}
            transition={STATUS_ENTER.transition}
            sx={{
                position: "relative",
                isolation: "isolate",
                width: embedded ? "100%" : "auto",
                maxWidth: "100%",
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                alignItems: "stretch",
                overflow: "hidden",
                borderRadius: embedded ? 0 : "12px",
                border: embedded ? "none" : `1px solid ${LOBBY_OURO_BORDER}`,
                background: embedded ? "transparent" : LOBBY_OURO_SURFACE,
                backdropFilter: embedded ? "none" : "blur(18px)",
                WebkitBackdropFilter: embedded ? "none" : "blur(18px)",
                boxShadow: embedded ? "none" : LOBBY_OURO_SHADOW,
                px: embedded
                    ? { xs: "0.1rem", xl: "0.35rem" }
                    : { xs: "0.8rem", xl: "1rem" },
                py: embedded
                    ? { xs: "0.15rem", xl: "0.2rem" }
                    : { xs: "0.7rem", xl: "0.78rem" },
                boxSizing: "border-box",
                "@keyframes lobbySignalLive": {
                    "0%, 100%": { opacity: 0.72 },
                    "50%": { opacity: 1 },
                },
                "&::before": {
                    content: '""',
                    display: embedded ? "none" : "block",
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "42%",
                    height: "1px",
                    background: LOBBY_OURO_TOP_GLOW,
                    boxShadow: `0 0 18px ${alpha(colors.primary.main, 0.45)}`,
                    pointerEvents: "none",
                    zIndex: 2,
                },
                "&::after": {
                    content: '""',
                    display: embedded ? "none" : "block",
                    position: "absolute",
                    left: "18%",
                    right: "18%",
                    bottom: "-2rem",
                    height: "3rem",
                    background: `radial-gradient(ellipse, ${alpha(colors.primary.main, 0.07)}, transparent 70%)`,
                    filter: "blur(16px)",
                    pointerEvents: "none",
                    zIndex: -1,
                },
                [MOBILE_NAV_MQ]: {
                    px: embedded ? 0 : "0.3rem",
                    py: embedded ? "0.12rem" : "0.62rem",
                },
            }}
        >
            {items.map(({ label, mobileLabel, icon: Icon, value, color, isLive }, index) => (
                <Box
                    key={label}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        minWidth: 0,
                        minHeight: "2.4rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: { xs: "0.52rem", xl: "0.6rem" },
                        px: { xs: "1rem", xl: "1.25rem" },
                        "&::after":
                            index < items.length - 1
                                ? {
                                      content: '""',
                                      position: "absolute",
                                      top: "8%",
                                      right: 0,
                                      width: "1px",
                                      height: "84%",
                                      background: `
                                          linear-gradient(
                                              180deg,
                                              transparent,
                                              rgba(255,255,255,0.12),
                                              transparent
                                          )
                                      `,
                                  }
                                : undefined,
                        [MOBILE_NAV_MQ]: {
                            minHeight: "2.05rem",
                            gap: "0.3rem",
                            px: "0.3rem",
                        },
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: "relative",
                            width: { xs: "1.8rem", xl: "1.95rem" },
                            height: { xs: "1.8rem", xl: "1.95rem" },
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "8px",
                            border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
                            background: `
                                linear-gradient(
                                    180deg,
                                    ${alpha(colors.primary.main, 0.12)},
                                    rgba(255,255,255,0.025)
                                )
                            `,
                            boxShadow: `
                                inset 0 1px 0 rgba(255,255,255,0.05),
                                0 6px 16px rgba(0,0,0,0.24),
                                0 0 18px ${alpha(colors.primary.main, 0.07)}
                            `,
                            [MOBILE_NAV_MQ]: {
                                width: "1.45rem",
                                height: "1.45rem",
                                borderRadius: "6px",
                            },
                        }}
                    >
                        <Icon
                            sx={{
                                fontSize: { xs: "0.95rem", xl: "1.05rem" },
                                color: colors.accent.main,
                                filter: `drop-shadow(0 0 6px ${alpha(colors.primary.main, 0.3)})`,
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "0.8rem",
                                },
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                right: "-2px",
                                bottom: "-2px",
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: color,
                                border: "2px solid rgba(8,8,10,0.96)",
                                boxShadow: `0 0 8px ${alpha(color, 0.5)}`,
                                animation: isLive
                                    ? "lobbySignalLive 2.4s ease-in-out infinite"
                                    : "none",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "0.16rem",
                        }}
                    >
                        <Typography
                            sx={{
                                color: colors.text.primary,
                                fontSize: { xs: "0.92rem", xl: "1.02rem" },
                                fontWeight: 700,
                                lineHeight: 1,
                                fontVariantNumeric: "tabular-nums",
                                textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "0.76rem",
                                },
                            }}
                        >
                            {value}
                        </Typography>
                        <Typography
                            sx={{
                                color: colors.text.secondary,
                                fontSize: "0.52rem",
                                fontWeight: 600,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                [MOBILE_NAV_MQ]: {
                                    fontSize: "0.44rem",
                                    letterSpacing: "0.06em",
                                },
                            }}
                        >
                            <Box component="span" sx={{ [MOBILE_NAV_MQ]: { display: "none" } }}>
                                {label}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    display: "none",
                                    [MOBILE_NAV_MQ]: { display: "inline" },
                                }}
                            >
                                {mobileLabel || label}
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

SystemStatusBar.propTypes = {
    platformStats: PropTypes.object,
    embedded: PropTypes.bool,
};
